import React from "react";
import { Box, Paper, Skeleton, Typography, Button, Chip } from "@mui/material";
import {
  ChevronRight,
  Store as StoreIcon,
  LocalOffer,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

interface Store {
  id: string;
  name: string;
  category: string;
  floor: string;
  activeOffers?: number;
}

interface MallHubSearchDropdownProps {
  isSearching: boolean;
  hasSearchResults: boolean;
  debouncedSearchQuery: string;
  searchStores: Store[];
  mallName: string;
  searchQuery: string;
  onClose: () => void;
}

/**
 * MallHubSearchDropdown Component
 *
 * Displays search results dropdown with loading states
 */
const MallHubSearchDropdown: React.FC<MallHubSearchDropdownProps> = ({
  isSearching,
  hasSearchResults,
  debouncedSearchQuery,
  searchStores,
  mallName,
  searchQuery,
  onClose,
}) => {
  const navigate = useNavigate();

  return (
    <Paper
      elevation={12}
      sx={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        mt: 1,
        maxHeight: 400,
        overflowY: "auto",
        zIndex: 1400,
        borderRadius: { xs: 2, md: 3 },
        maxWidth: { xs: "100%", sm: 700, md: 800 },
        width: "100%",
      }}
    >
      {isSearching ? (
        <Box sx={{ p: 2 }}>
          {[...Array(3)].map((_, index) => (
            <Box
              key={index}
              sx={{
                p: 1.5,
                display: "flex",
                gap: 2,
                alignItems: "center",
              }}
            >
              <Skeleton variant="circular" width={40} height={40} />
              <Box sx={{ flex: 1 }}>
                <Skeleton
                  variant="text"
                  width="60%"
                  sx={{ fontSize: "1rem", mb: 0.5 }}
                />
                <Skeleton variant="text" width="40%" />
              </Box>
            </Box>
          ))}
        </Box>
      ) : debouncedSearchQuery && hasSearchResults ? (
        <Box sx={{ p: 2, zIndex: 1400 }}>
          {/* Stores Results */}
          {searchStores.length > 0 && (
            <Box>
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  px: 1,
                  mb: 1,
                  color: "text.secondary",
                  fontWeight: 600,
                }}
              >
                🏪{" "}
                {`Found ${searchStores.length} Store${
                  searchStores.length !== 1 ? "s" : ""
                } in ${mallName}`}
              </Typography>
              {searchStores.slice(0, 3).map((store) => (
                <Box
                  key={store.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    border: "1px solid",
                    borderColor: "transparent",
                    "&:hover": {
                      bgcolor: "primary.50",
                      borderColor: "primary.main",
                      transform: "translateX(4px)",
                      "& .click-indicator": {
                        opacity: 1,
                        transform: "translateX(0)",
                      },
                    },
                  }}
                  onClick={() => {
                    navigate(`/stores/${store.id}`);
                    onClose();
                  }}
                >
                  {/* Store Icon */}
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      bgcolor: "primary.50",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <StoreIcon sx={{ fontSize: 20, color: "primary.main" }} />
                  </Box>

                  {/* Store Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {store.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {store.floor}
                      </Typography>
                      {(store.activeOffers ?? 0) > 0 && (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            •
                          </Typography>
                          <Chip
                            icon={<LocalOffer sx={{ fontSize: 12 }} />}
                            label={`${store.activeOffers} Offer${
                              (store.activeOffers ?? 0) > 1 ? "s" : ""
                            }`}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.688rem",
                              bgcolor: "success.50",
                              color: "success.main",
                              "& .MuiChip-icon": {
                                color: "success.main",
                              },
                            }}
                          />
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Click Indicator */}
                  <ChevronRight
                    className="click-indicator"
                    sx={{
                      fontSize: 24,
                      color: "primary.main",
                      opacity: 0.5,
                      transform: "translateX(-4px)",
                      transition: "all 0.2s ease",
                      flexShrink: 0,
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}

          {/* View All Button */}
          <Button
            fullWidth
            variant="outlined"
            sx={{ mt: 2 }}
            onClick={() => {
              navigate(`/stores?search=${searchQuery}`);
              onClose();
            }}
          >
            View All Stores
          </Button>
        </Box>
      ) : debouncedSearchQuery ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography color="text.secondary">
            No results found for "{debouncedSearchQuery}"
          </Typography>
        </Box>
      ) : null}
    </Paper>
  );
};

export default MallHubSearchDropdown;
